import { useCallback, useState } from 'react';
import Papa from 'papaparse';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Alert, AlertDescription } from '../ui/alert.tsx';
import { AlertCircle, CheckCircle2, FileText, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { validatePropertyForm } from '../../validators/property.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { getApiUrl } from '../../config/api.ts';
import { createLogger, toErrorMessage } from '@metron/shared';

const logger = createLogger('ImportPropertiesModal');

interface CSVRow {
  'account name*'?: string;
  'metron id'?: string;
}

interface ParsedProperty {
  name: string;
  metronId: string;
  rowNumber: number;
}

interface ValidationError {
  rowNumber: number;
  errors: string[];
}

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ rowNumber: number; error: string }>;
}

interface BatchResultItem {
  success: boolean;
  property?: {
    id: number;
    name: string;
    customerId: number;
    metronId: string;
  };
  error?: string;
  metronId: string;
  name: string;
}

interface ZodIssue {
  message: string;
  path: string[];
  code: string;
}

interface ImportPropertiesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: number;
  onImportComplete: () => void;
}

export function ImportPropertiesModal({
  open,
  onOpenChange,
  customerId,
  onImportComplete,
}: ImportPropertiesModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [validProperties, setValidProperties] = useState<ParsedProperty[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importProgress, setImportProgress] = useState<
    {
      current: number;
      total: number;
    } | null
  >(null);

  const parseCSV = useCallback((content: string): CSVRow[] => {
    const result = Papa.parse<Record<string, string>>(content, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.toLowerCase().trim(),
    });

    if (result.errors.length > 0) {
      logger.error({ errors: result.errors }, 'CSV parsing errors');
    }

    return result.data as CSVRow[];
  }, []);

  const validateCSV = useCallback((rows: CSVRow[]): {
    valid: ParsedProperty[];
    errors: ValidationError[];
  } => {
    const valid: ParsedProperty[] = [];
    const errors: ValidationError[] = [];

    // Check for required columns
    if (rows.length > 0) {
      const firstRow = rows[0];
      const hasNameColumn = 'account name*' in firstRow;
      const hasMetronIdColumn = 'metron id' in firstRow;

      if (!hasNameColumn || !hasMetronIdColumn) {
        const missingColumns: string[] = [];
        if (!hasNameColumn) missingColumns.push('Account Name*');
        if (!hasMetronIdColumn) missingColumns.push('Metron ID');

        errors.push({
          rowNumber: 0,
          errors: [`Missing required columns: ${missingColumns.join(', ')}`],
        });
        return { valid, errors };
      }
    }

    // Validate each row
    rows.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because: +1 for 1-based indexing, +1 for header row
      const rowErrors: string[] = [];

      const name = row['account name*'] || '';
      const metronId = row['metron id'] || '';

      // Validate using the property validator
      const validation = validatePropertyForm({ name, metronId });

      if (validation.name) rowErrors.push(validation.name);
      if (validation.metronId) rowErrors.push(validation.metronId);

      // Check for max length
      if (name.length > 255) {
        rowErrors.push('Property name must be 255 characters or less');
      }

      if (rowErrors.length > 0) {
        errors.push({ rowNumber, errors: rowErrors });
      } else {
        valid.push({ name, metronId, rowNumber });
      }
    });

    return { valid, errors };
  }, []);

  const handleFileChange = useCallback(
    (selectedFile: File | null) => {
      if (!selectedFile) {
        setFile(null);
        setValidProperties([]);
        setValidationErrors([]);
        setImportResult(null);
        return;
      }

      // Check file type
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }

      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        toast.error('File size must be 5MB or less');
        return;
      }

      setFile(selectedFile);
      setImportResult(null);

      // Parse and validate CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const rows = parseCSV(content);

          if (rows.length === 0) {
            toast.error('CSV file is empty');
            setFile(null);
            return;
          }

          const { valid, errors } = validateCSV(rows);
          setValidProperties(valid);
          setValidationErrors(errors);

          if (errors.length > 0) {
            toast.warning(`Found ${errors.length} validation error(s)`);
          } else {
            toast.success(`Found ${valid.length} valid propert${valid.length === 1 ? 'y' : 'ies'}`);
          }
        } catch (error) {
          toast.error('Failed to parse CSV file');
          setFile(null);
          logger.error({ error }, 'CSV parse error');
        }
      };
      reader.readAsText(selectedFile);
    },
    [parseCSV, validateCSV],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileChange(droppedFile);
      }
    },
    [handleFileChange],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const { getAccessToken } = useAuth();

  const handleImport = useCallback(async () => {
    if (validProperties.length === 0) {
      toast.error('No valid properties to import');
      return;
    }

    setIsLoading(true);
    setImportProgress({
      current: 0,
      total: validProperties.length,
    });

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }

      const response = await fetch(getApiUrl('properties/batch'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          properties: validProperties.map((p) => ({
            name: p.name,
            metronId: p.metronId,
            customerId,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json() as {
          error?: {
            issues?: ZodIssue[];
            message?: string;
          };
        };
        // Handle Zod validation errors
        if (errorData.error?.issues) {
          const errorMessage = errorData.error.issues
            .map((issue) => issue.message)
            .join(', ');
          throw new Error(errorMessage);
        }
        throw new Error(
          errorData.error?.message || `Failed to import properties: ${response.statusText}`,
        );
      }

      const data = await response.json() as {
        results: BatchResultItem[];
        summary: {
          successful: number;
          failed: number;
        };
      };

      // Update progress to complete
      setImportProgress({
        current: validProperties.length,
        total: validProperties.length,
      });

      // Build result from batch response
      const result: ImportResult = {
        success: data.summary.successful,
        failed: data.summary.failed,
        errors: data.results
          .filter((r) => !r.success)
          .map((r) => {
            // Find the original row number for this property
            const originalProperty = validProperties.find(
              (p) => p.name === r.name && p.metronId === r.metronId,
            );
            return {
              rowNumber: originalProperty?.rowNumber ?? 0,
              error: r.error ?? 'Unknown error',
            };
          }),
      };

      setImportProgress(null);
      setImportResult(result);

      if (result.success > 0) {
        onImportComplete();
      }

      if (result.failed === 0) {
        toast.success(
          `Successfully imported ${result.success} propert${result.success === 1 ? 'y' : 'ies'}`,
        );
      } else if (result.success > 0) {
        toast.warning(
          `Imported ${result.success} propert${
            result.success === 1 ? 'y' : 'ies'
          }, ${result.failed} failed`,
        );
      } else {
        toast.error('Failed to import properties');
      }
    } catch (error) {
      logger.error({ error }, 'Import error');
      toast.error(toErrorMessage(error, 'Failed to import properties'));
      setImportProgress(null);
    } finally {
      setIsLoading(false);
    }
  }, [validProperties, customerId, getAccessToken, onImportComplete]);

  const handleClose = useCallback(() => {
    setFile(null);
    setValidProperties([]);
    setValidationErrors([]);
    setImportResult(null);
    setImportProgress(null);
    setIsLoading(false);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Import Properties from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import properties. Required columns: Account Name* and Metron
            ID.
          </DialogDescription>
        </DialogHeader>

        {!importResult
          ? (
            <div className='grid gap-4 py-4'>
              {/* File Upload Area */}
              <div className='grid gap-2'>
                <Label htmlFor='csv-file'>CSV File</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {file
                    ? (
                      <div className='flex flex-col items-center gap-2'>
                        <FileText className='h-12 w-12 text-muted-foreground' />
                        <p className='text-sm font-medium'>{file.name}</p>
                        <p className='text-xs text-muted-foreground'>
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => handleFileChange(null)}
                          disabled={isLoading}
                        >
                          Remove
                        </Button>
                      </div>
                    )
                    : (
                      <>
                        <Upload className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
                        <p className='text-sm font-medium mb-2'>
                          Drag and drop CSV file here, or click to select
                        </p>
                        <Input
                          id='csv-file'
                          type='file'
                          accept='.csv'
                          className='max-w-xs mx-auto'
                          onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                          disabled={isLoading}
                        />
                        <p className='text-xs text-muted-foreground mt-2'>
                          Maximum file size: 5MB
                        </p>
                      </>
                    )}
                </div>
              </div>

              {/* Import Progress */}
              {importProgress && (
                <div className='space-y-2'>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='font-medium'>Importing properties...</span>
                    <span className='text-muted-foreground'>
                      {importProgress.current} / {importProgress.total}
                    </span>
                  </div>
                  <div className='w-full bg-secondary rounded-full h-2.5'>
                    <div
                      className='bg-primary h-2.5 rounded-full transition-all duration-300'
                      style={{
                        width: `${(importProgress.current / importProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Validation Summary */}
              {file && !importProgress && (
                <div className='space-y-3'>
                  {validProperties.length > 0 && (
                    <Alert>
                      <CheckCircle2 className='h-4 w-4' />
                      <AlertDescription>
                        Found {validProperties.length}{' '}
                        valid propert{validProperties.length === 1 ? 'y' : 'ies'} to import
                      </AlertDescription>
                    </Alert>
                  )}

                  {validationErrors.length > 0 && (
                    <Alert variant='destructive'>
                      <AlertCircle className='h-4 w-4' />
                      <AlertDescription>
                        <div className='font-medium mb-2'>
                          Found {validationErrors.length} validation error(s):
                        </div>
                        <div className='space-y-1 text-xs max-h-32 overflow-y-auto'>
                          {validationErrors.map((error, index) => (
                            <div key={index}>
                              <span className='font-semibold'>
                                Row {error.rowNumber}:
                              </span>{' '}
                              {error.errors.join(', ')}
                            </div>
                          ))}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )
          : (
            <div className='grid gap-4 py-4'>
              {/* Import Results */}
              <div className='space-y-3'>
                {importResult.success > 0 && (
                  <Alert>
                    <CheckCircle2 className='h-4 w-4' />
                    <AlertDescription>
                      Successfully imported {importResult.success}{' '}
                      propert{importResult.success === 1 ? 'y' : 'ies'}
                    </AlertDescription>
                  </Alert>
                )}

                {importResult.failed > 0 && (
                  <Alert variant='destructive'>
                    <XCircle className='h-4 w-4' />
                    <AlertDescription>
                      <div className='font-medium mb-2'>
                        Failed to import {importResult.failed}{' '}
                        propert{importResult.failed === 1 ? 'y' : 'ies'}:
                      </div>
                      <div className='space-y-1 text-xs max-h-32 overflow-y-auto'>
                        {importResult.errors.map((error, index) => (
                          <div key={index}>
                            <span className='font-semibold'>Row {error.rowNumber}:</span>{' '}
                            {error.error}
                          </div>
                        ))}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={handleClose}
            disabled={isLoading}
          >
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              type='button'
              onClick={handleImport}
              disabled={isLoading || validProperties.length === 0}
            >
              {isLoading
                ? `Importing ${validProperties.length} propert${
                  validProperties.length === 1 ? 'y' : 'ies'
                }...`
                : `Import ${validProperties.length} propert${
                  validProperties.length === 1 ? 'y' : 'ies'
                }`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
